import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import {
  categoryPensionOptions,
  defaultPensionMethodOptions,
  optStateNestPensionOptions,
  optStateNowPensionOptions,
  optStatePensionOptions,
  pensionDeductionMethods,
  PensionDeductionMethodsEnum,
  pensionProviderIdOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import {
  isNowLikePensionProvider,
  NestConnectionStatusEnum,
  PensionProvidersNameEnum,
  PensionSelfCertificationEnum,
  PENSION_UNCERTIFIED_MINS,
  getPensionSelfCertMins,
  readNestSelfCertification,
} from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useGetPensionByEmployeeId,
  useUpdatePensionByEmployeeId,
} from '@nxsys/hooks/api';
import { UpdatePensionByEmployeeIdParams } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { IPension } from '@nxsys/types/models/pension';
import {
  pensionCalCulationMethodOptions,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { fetchSmartPensionByEmployeeApi } from '@nxsys/services/api/employee';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { Col, Divider, Form, Modal, Row, Typography } from 'antd';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

type Props = {
  employeeData: EmployeeRecordType;
  readOnly?: boolean;
};

// A % input the user cleared: AppPercentageInput gives '' or null.
const isBlankPercent = (value: unknown) => value === '' || value === null;

// NX-5520: '' stands for the company's own NEST group, saved as null.
const companyDefaultNestOption = (groupName: string) => ({
  value: '',
  label: `Company default (${groupName})`,
});

// NX-5520: '' stands for the company's pension method, saved as null.
const companyDefaultMethodOption = (pensionMethod?: string | null) => ({
  value: '',
  label: `Company default (${
    defaultPensionMethodOptions.find(
      (o) => o.value === (pensionMethod || 'QUALIFYING_EARNINGS'),
    )?.label
  })`,
});

const pensionMethodLabel = (pensionMethod?: string | null) =>
  defaultPensionMethodOptions.find((o) => o.value === pensionMethod)?.label ??
  '';

const Pension = ({ employeeData, readOnly }: Props) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const currency = showCurrencyByCountry(payrollCountry);
  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);
  const [showMainYTD, setShowMainYTD] = useState(false);
  const [showAvcYTD, setShowAvcYTD] = useState(false);
  const { run: updatePension, loading: isUpdatingPension } =
    useUpdatePensionByEmployeeId();
  const [isFetchingSmartPension, setIsFetchingSmartPension] = useState(false);

  const {
    run: getPension,
    value: pensionValue,
    loading: isFetchingPension,
  } = useGetPensionByEmployeeId();
  const pensionData = useMemo(() => pensionValue?.data, [pensionValue]);

  useEffect(() => {
    getPension(employeeData.id);
  }, [employeeData]);

  useEffect(() => {
    if (pensionData) {
      const d = pensionData;
      // NX-5017 / NX-5231: ER%/EE% cascade — the worker's own column, then the
      // company's, then the statutory 3%/5%, matching the fallback the company
      // pension tab shows so the two screens agree. Filling the input is all
      // it does: a % reaches the employee row only when the company has one to
      // differ from, so 3%/5% can never be pinned onto them.
      // `typeof === 'number'` keeps an explicit 0 and drops odd serializations.
      const autoEnrolSettings =
        employeeData?.companyPayment?.autoEnrolSettings;
      // NX-5770: NEST is the exception — its rates live on the employee's
      // payment group, filled in by the effect near defaultPercents. These
      // columns hold the wizard's 3%/5% seed and are never updated for NEST.
      const companyOwnsPercent =
        autoEnrolSettings?.pensionProviderId !==
        PensionProvidersNameEnum.NEST_PENSION;
      const { totalMin, erMin } = PENSION_UNCERTIFIED_MINS;
      const companyPercent = (value: unknown, statutory: number) =>
        !companyOwnsPercent
          ? null
          : typeof value === 'number'
            ? value
            : statutory;
      const erPercent =
        typeof d.erPensionPercentage === 'number'
          ? d.erPensionPercentage
          : companyPercent(autoEnrolSettings?.erPensionPercentage, erMin);
      const eePercent =
        typeof d.eePensionPercentage === 'number'
          ? d.eePensionPercentage
          : companyPercent(
              autoEnrolSettings?.eePensionPercentage,
              totalMin - erMin,
            );
      form.setFieldsValue({
        // Auto Enrolment Status
        category: d.category
          ? (categoryPensionOptions.find((o) => o.value === d.category) ?? null)
          : null,
        assessStatus: d.assessStatus ?? '',
        dateOnClassifiedOn: d.dateOnClassifiedOn
          ? moment(d.dateOnClassifiedOn)
          : null,
        lastAssessmentOn: d.lastAssessmentOn
          ? moment(d.lastAssessmentOn)
          : null,
        pensionProviderId: d.pensionProviderId
          ? (pensionProviderIdOptions.find(
              (o) => o.value === d.pensionProviderId,
            ) ?? null)
          : null,
        memberId: d.memberId ?? '',
        postponementEndDate: d.postponementEndDate
          ? moment(d.postponementEndDate)
          : null,
        membershipStartDate: d.membershipStartDate
          ? moment(d.membershipStartDate)
          : null,
        otpState: d.otpState
          ? (optStatePensionOptions.find((o) => o.value === d.otpState) ?? null)
          : null,
        otpInDate: d.otpInDate ? moment(d.otpInDate) : null,
        otpOutDate: d.otpOutDate ? moment(d.otpOutDate) : null,
        optOutReference: d.optOutReference ?? '',
        // SMART/NEST extra fields
        pensionSchemeId: d.pensionSchemeId ?? '',
        pensionSchemeName: d.pensionSchemeName ?? '',
        nestEmployerRef: d.nestEmployerRef ?? '',
        enrolmentType: d.enrolmentType ?? '',
        nestGroupOverride: d.nestGroupOverride
          ? { value: d.nestGroupOverride, label: d.nestGroupOverride }
          : d.nestCompanyGroup?.groupName
            ? companyDefaultNestOption(d.nestCompanyGroup.groupName)
            : null,
        nestPaymentSource: d.nestPaymentSource ?? '',
        // Calculation Method, Pension Method & Salary Sacrifice
        pensionMethod:
          defaultPensionMethodOptions.find(
            (o) => o.value === d.pensionMethod,
          ) ?? companyDefaultMethodOption(autoEnrolSettings?.pensionMethod),
        pensionCalculationType: d.pensionCalculationType
          ? (pensionCalCulationMethodOptions.find(
              (o) => o.value === d.pensionCalculationType,
            ) ?? null)
          : null,
        // null = not set on the employee, so the company setting
        // applies. Defaulting it to false saved an override on every save.
        isSalarySacrifice: d.isSalarySacrifice ?? null,
        // Pension Deduction Method
        erPensionDeductionMethod: d.erPensionDeductionMethod
          ? (pensionDeductionMethods.find(
              (o) => o.value === d.erPensionDeductionMethod,
            ) ?? null)
          : null,
        eePensionDeductionMethod: d.eePensionDeductionMethod
          ? (pensionDeductionMethods.find(
              (o) => o.value === d.eePensionDeductionMethod,
            ) ?? null)
          : null,
        erPensionFixedAmount: d.erPensionFixedAmount?.toString() ?? '',
        eePensionFixedAmount: d.eePensionFixedAmount?.toString() ?? '',
        // Additional Voluntary Contribution
        avcERContribution: d.avcERContribution ?? '',
        avcEEContribution: d.avcEEContribution ?? '',
        // Pension Refund
        refundPaidDate: d.refundPaidDate ? moment(d.refundPaidDate) : null,
        refundDue: d.refundDue ?? false,
        refundPaidER: d.refundPaidER ?? '',
        refundPaidEE: d.refundPaidEE ?? '',
      });
      // NX-5993: fresh server data replaces whatever was typed, so clear
      // `touched` too — otherwise, after saving a NEST % equal to the group
      // rate (stored as null), the group seed below skips the field as
      // "typed in" and the input is left empty.
      form.setFields([
        {
          name: 'erPensionPercentage',
          value: erPercent === null ? '' : erPercent.toString(),
          touched: false,
        },
        {
          name: 'eePensionPercentage',
          value: eePercent === null ? '' : eePercent.toString(),
          touched: false,
        },
      ]);
    }
  }, [pensionData]);

  // Watched values for conditional rendering
  const selectedProvider = watchValues?.pensionProviderId?.value;
  const isSmartPension =
    selectedProvider === PensionProvidersNameEnum.SMART_PENSION;
  const isNestPension =
    selectedProvider === PensionProvidersNameEnum.NEST_PENSION;
  const isNowLike = isNowLikePensionProvider(selectedProvider ?? '');

  // NX-4481: Hide Opt-In fields when Auto Enrolled (Smart Pension only)
  const activePostponement =
    watchValues?.postponementEndDate &&
    moment().valueOf() < moment(watchValues.postponementEndDate).valueOf();
  const isAutoEnrolled =
    watchValues?.category?.value === 'Eligible Jobholder' &&
    !activePostponement;
  const isOptedIn = pensionData?.otpState === 'opted_in';
  const isNotShowOptInState = isSmartPension && isAutoEnrolled && isOptedIn;

  const smartPensionOtpOptions = isNotShowOptInState
    ? optStatePensionOptions.filter((o) => o.value !== 'opted_in')
    : optStatePensionOptions;

  // Clear otpState/otpInDate when NX-4481 triggers
  useEffect(() => {
    if (isNotShowOptInState) {
      form.setFieldsValue({ otpState: null, otpInDate: null });
    }
  }, [isNotShowOptInState]);

  // Smart Pension fetch eligibility
  const companyPayment = employeeData?.companyPayment as any;
  const isFetchSmartPension =
    isSmartPension &&
    !!companyPayment?.smartPensionAccessToken &&
    !!companyPayment?.smartPensionCompanyId;

  // When the company manages Smart Pension manually, the SMART fields that are
  // normally sourced from the SMART API become editable (like NOW providers).
  const isManagePensionManually =
    !!companyPayment?.autoEnrolSettings?.managePensionManually;
  // Fields are locked only when the provider is API-managed (not manual).
  const isSmartManaged = isSmartPension && !isManagePensionManually;
  const isNestManaged = isNestPension && !isManagePensionManually;
  // NOW-like providers and manually-managed SMART/NEST all allow free editing
  // of the provider-sourced fields (opt-in/opt-out, postponement, etc.).
  const isNowLikeOrManual =
    isNowLike ||
    ((isSmartPension || isNestPension) && isManagePensionManually);

  // Shared postponement + membership start row (opt fields differ only by the
  // `disabled` flags, which are already provider-aware).
  const renderPostponementRow = () => (
    <Row gutter={24}>
      <Col xs={24} sm={12}>
        <Form.Item
          label={messages['common.postponementEndDate']}
          name="postponementEndDate"
        >
          <AppDatePicker
            placeholder={messages['form.selectDate']}
            format={'DD/MM/YYYY'}
            disabled={readOnly || !isNowLikeOrManual}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <Form.Item
          label={messages['common.membershipStartDate']}
          name="membershipStartDate"
        >
          <AppDatePicker
            placeholder={messages['form.selectDate']}
            format={'DD/MM/YYYY'}
            disabled={readOnly || isNestManaged}
          />
        </Form.Item>
      </Col>
    </Row>
  );

  // SMART_PENSION auto-enrolment fields.
  const renderSmartAutoEnrolment = () => (
    <>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.pensionSchemeId']}
            name="pensionSchemeId"
          >
            <AppInput
              placeholder={messages['form.enter']}
              disabled={readOnly || !isManagePensionManually}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.pensionSchemeName']}
            name="pensionSchemeName"
          >
            <AppInput
              placeholder={messages['form.enter']}
              disabled={readOnly || !isManagePensionManually}
            />
          </Form.Item>
        </Col>
      </Row>
      {renderPostponementRow()}
      <Form.Item label={messages['common.otpState']} name="otpState">
        <AppSelect
          options={
            isNowLikeOrManual
              ? optStateNowPensionOptions
              : smartPensionOtpOptions
          }
          className="w-full !min-w-0"
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Row gutter={24}>
        {!isNotShowOptInState && (
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.otpInDate']} name="otpInDate">
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
                disabled={
                  readOnly ||
                  (!isNowLikeOrManual &&
                    watchValues?.otpState?.value !== 'opted_in' &&
                    watchValues?.otpState?.value !== 'rejoined')
                }
                value={isNotShowOptInState ? null : watchValues?.otpInDate}
              />
            </Form.Item>
          </Col>
        )}
        <Col xs={24} sm={12}>
          <Form.Item label={messages['common.otpOutDate']} name="otpOutDate">
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format={'DD/MM/YYYY'}
              disabled={readOnly || !isNowLikeOrManual}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // NEST_PENSION auto-enrolment fields (all sourced from NEST, read-only).
  const renderNestAutoEnrolment = () => (
    <>
      <Form.Item
        label={messages['common.nestEmployerId']}
        name="nestEmployerRef"
      >
        <AppInput
          placeholder={messages['form.enter']}
          disabled={readOnly || isNestManaged}
        />
      </Form.Item>
      {renderPostponementRow()}
      <Form.Item label={messages['common.enrolmentType']} name="enrolmentType">
        <AppInput
          placeholder={messages['form.enter']}
          disabled={readOnly || isNestManaged}
        />
      </Form.Item>
      <Form.Item label={messages['common.otpState']} name="otpState">
        <AppSelect
          options={optStateNestPensionOptions}
          className="w-full !min-w-0"
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.otpInDate']}
            name="otpInDate"
            {...(watchValues?.otpState?.value === 'opted_in'
              ? {
                  rules: [
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ],
                }
              : {})}
          >
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format={'DD/MM/YYYY'}
              disabled={
                readOnly ||
                (!isManagePensionManually &&
                  watchValues?.otpState?.value !== 'opted_in')
              }
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          {/* NX-5679: Nest never sends the opt-out date back for paper opt-out
              notices, so hiding this field left opted-out workers with no
              visible date. Read-only only while the company is actually
              CONNECTED (the retrieve-opt-outs sync only runs for connected
              companies and owns the value there); everywhere else — manual
              mode or disconnected — a person is the only possible writer. */}
          <Form.Item label={messages['common.otpOutDate']} name="otpOutDate">
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format={'DD/MM/YYYY'}
              disabled={
                readOnly ||
                (isNestManaged &&
                  companyPayment?.nestConnectionStatus ===
                    NestConnectionStatusEnum.CONNECTED)
              }
            />
          </Form.Item>
        </Col>
      </Row>
      {/*
        NX-3476: Nest has no opt-out API — the opt-out is reported on the next
        contribution schedule as a reason-9 row, which Nest only accepts with the
        reference from the member's paper notice. Nest matches the value against its
        own record, so it must be typed exactly as printed (leading zeros included)
        and must never be trimmed or reformatted.
      */}
      {watchValues?.otpState?.value === 'opted_out' && (
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Opt-Out Reference"
              name="optOutReference"
              extra={
                'Enter the reference exactly as printed on the member\'s "Notice ' +
                'to opt out of pension saving" form, including any leading zeros.'
              }
              rules={[
                { required: true, message: messages['form.pleaseEnter'] },
              ]}
            >
              {/* Nest matches this against its own record, so it must survive
                  verbatim — AppInput capitalises the first letter by default. */}
              <AppInput
                allowCapitalize={false}
                placeholder="e.g. 001234"
                maxLength={20}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          {pensionData?.optOutNotifiedToNestAt && (
            <Col xs={24} sm={12}>
              <Form.Item label="Opt-Out Reported To Nest">
                <AppInput
                  disabled
                  value={moment(pensionData.optOutNotifiedToNestAt).format(
                    'DD/MM/YYYY',
                  )}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      )}
      {/* NX-5520: payroll and enrolment use the group picked here; the
          pension method follows the group's earnings basis. */}
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.pensionGroup']}
            name="nestGroupOverride"
            validateStatus={nestGroupOverrideError ? 'error' : undefined}
            help={nestGroupOverrideError}
            extra={
              pensionData?.nestGroupChangeBlockedReason ??
              (isManagePensionManually
                ? 'Payroll uses this group. Keep the member in the same group in NEST.'
                : undefined)
            }
          >
            <AppSelect
              options={nestGroupSelectOptions}
              className="w-full !min-w-0"
              placeholder={messages['form.select']}
              allowClear={false}
              disabled={readOnly || !!pensionData?.nestGroupChangeBlockedReason}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label={messages['common.paymentSource']}
            name="nestPaymentSource"
          >
            <AppInput
              placeholder={messages['form.enter']}
              disabled={readOnly || isNestManaged}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  // NOW-like (and other non-SMART/non-NEST) auto-enrolment fields.
  const renderNowLikeAutoEnrolment = () => (
    <>
      {renderPostponementRow()}
      <Form.Item label={messages['common.otpState']} name="otpState">
        <AppSelect
          options={
            isNowLikeOrManual
              ? optStateNowPensionOptions
              : smartPensionOtpOptions
          }
          className="w-full !min-w-0"
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Row gutter={24}>
        {!isNotShowOptInState && (
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.otpInDate']} name="otpInDate">
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
                disabled={
                  readOnly ||
                  (!isNowLikeOrManual &&
                    watchValues?.otpState?.value !== 'opted_in' &&
                    watchValues?.otpState?.value !== 'rejoined')
                }
                value={isNotShowOptInState ? null : watchValues?.otpInDate}
              />
            </Form.Item>
          </Col>
        )}
        <Col xs={24} sm={12}>
          <Form.Item label={messages['common.otpOutDate']} name="otpOutDate">
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format={'DD/MM/YYYY'}
              disabled={readOnly || !isNowLikeOrManual}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const handleFetchSmartPensionForEmployee = async () => {
    if (!employeeData?.id) return;
    try {
      setIsFetchingSmartPension(true);
      await fetchSmartPensionByEmployeeApi({
        subcontractorId: employeeData.id,
      });
      showMessage({
        type: 'success',
        content:
          messages['common.smartPensionFetchSuccess'] ??
          'Smart Pension was fetched for employee successfully',
      });
      getPension(employeeData.id);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsFetchingSmartPension(false);
    }
  };

  // ER/EE deduction method conditional
  const erMethod = watchValues?.erPensionDeductionMethod?.value;
  const eeMethod = watchValues?.eePensionDeductionMethod?.value;

  // NX-5520: the groups of the employee's pay frequency, the company's first.
  const nestGroupSelectOptions = useMemo(() => {
    const companyGroup = pensionData?.nestCompanyGroup?.groupName;
    const others = (pensionData?.nestGroupOptions ?? [])
      .filter((o) => o.groupName !== companyGroup)
      .map((o) => ({ value: o.groupName, label: o.groupName }));
    return companyGroup
      ? [companyDefaultNestOption(companyGroup), ...others]
      : others;
  }, [pensionData]);

  // NX-5520: the saved group can no longer be used; shown until another group
  // is picked.
  const nestGroupOverrideError =
    pensionData?.nestGroupOverrideError &&
    (watchValues?.nestGroupOverride === undefined ||
      watchValues.nestGroupOverride?.value === pensionData.nestGroupOverride)
      ? pensionData.nestGroupOverrideError
      : undefined;

  // NX-5231 / NX-5520: the NEST group this employee is in — the one picked in
  // the form, else the company's group for their pay frequency. NEST keeps the
  // rates, the pension method and the certified set on the group, so all of
  // them are read from here.
  const nestGroupSettings = useMemo(() => {
    // Until the form is seeded, the saved group.
    const picked =
      watchValues?.nestGroupOverride === undefined
        ? pensionData?.nestGroupOverride
        : watchValues.nestGroupOverride?.value;
    return (
      (picked &&
        pensionData?.nestGroupOptions?.find((o) => o.groupName === picked)) ||
      pensionData?.nestCompanyGroup ||
      undefined
    );
  }, [pensionData, watchValues?.nestGroupOverride]);

  const nestGroupSelfCert =
    (readNestSelfCertification(nestGroupSettings) as
      | PensionSelfCertificationEnum
      | '') || undefined;

  // NX-5231: employee ER/EE % overrides are validated live against the
  // self-certification set that applies to them (parity with the employer
  // pension form and the BE save in subcontractor.pension.service).
  //
  // NOW-like and SMART certify the scheme once, so the set sits on the
  // company. NEST certifies per payment group and never uses the company-level
  // field, so the set has to come from the group this employee is assigned to.
  const applicableSelfCert = isNestPension
    ? nestGroupSelfCert
    : (employeeData?.companyPayment?.autoEnrolSettings
        ?.pensionSelfCertification as PensionSelfCertificationEnum | undefined);
  // Nothing certified → the pre-NX-5231 floors, unchanged from before the set
  // selector existed.
  const pensionMins = getPensionSelfCertMins(applicableSelfCert);

  // NX-5770: the % the inputs show when the employee has no override of their
  // own — the company's, or the statutory 3%/5% where it has none. NEST is the
  // exception: its rates live on the employee's payment group, and the company
  // columns are stuck at the create wizard's 3%/5% seed, never updated.
  //
  // The divergence warning on save compares against this.
  const defaultPercents = useMemo(() => {
    const asRate = (value: unknown) =>
      typeof value === 'number' ? value : null;
    const autoEnrol = employeeData?.companyPayment?.autoEnrolSettings;
    if (isNestPension) {
      return {
        er: asRate(nestGroupSettings?.employerRate),
        ee: asRate(nestGroupSettings?.employeeRate),
      };
    }
    const { totalMin, erMin } = PENSION_UNCERTIFIED_MINS;
    return {
      er: asRate(autoEnrol?.erPensionPercentage) ?? erMin,
      ee: asRate(autoEnrol?.eePensionPercentage) ?? totalMin - erMin,
    };
  }, [employeeData, isNestPension, nestGroupSettings]);

  // Fill the % with the group's rates — on load and when another group is
  // picked — skipping any field the employee overrides or has typed in.
  useEffect(() => {
    if (!isNestPension || !nestGroupSettings) return;
    const seed = (
      field: 'erPensionPercentage' | 'eePensionPercentage',
      own: unknown,
      rate: number | null,
    ) => {
      if (typeof own === 'number' || form.isFieldTouched(field)) return;
      form.setFieldValue(field, rate === null ? '' : rate.toString());
    };
    seed(
      'erPensionPercentage',
      pensionData?.erPensionPercentage,
      defaultPercents.er,
    );
    seed(
      'eePensionPercentage',
      pensionData?.eePensionPercentage,
      defaultPercents.ee,
    );
  }, [isNestPension, nestGroupSettings, pensionData, defaultPercents]);

  // NX-5770: the floors move with the applicable set (a NEST regroup, say),
  // so re-check the % fields or a valid 3%/4% pair keeps the old "at least
  // 8%" message. Touched fields only, to stay quiet on prefilled values.
  useEffect(() => {
    if (!form.isFieldsTouched(['erPensionPercentage', 'eePensionPercentage']))
      return;
    form
      .validateFields(['erPensionPercentage', 'eePensionPercentage'])
      .catch(() => {});
  }, [pensionMins.erMin, pensionMins.totalMin]);

  const doSave = async (params: UpdatePensionByEmployeeIdParams) => {
    try {
      await updatePension(employeeData.id, params);
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      // Re-seed the form + the percentages the change-confirmation compares against
      getPension(employeeData.id);
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!employeeData?.id) return;

      // NX-5993: always send the entered %; the backend stores a % matching
      // the company rate as null, so the employee keeps following company
      // changes and an old override can be taken back to the company rate.
      // Omitting it here kept the old override instead. A cleared input is
      // sent as null (follow the company) — toNumber('') would be 0, a real
      // rate. undefined means the input is not shown (not PERCENTAGE), so the
      // stored % is left as is.
      const percentToSave = (
        submitted: string | number | null | undefined,
      ): number | null | undefined => {
        if (submitted === undefined) return undefined;
        if (isBlankPercent(submitted)) return null;
        return toNumber(submitted);
      };

      // NX-5231: when Smart Pension owns the contribution setup (API-managed,
      // not manual) the ER/EE % inputs are read-only mirrors of what SMART
      // returned, so echoing them back would pin SMART's numbers onto the
      // employee row and leave it stale the moment SMART changes them. Only
      // the percentages are affected — deduction methods and fixed amounts
      // have no company-level counterpart, so they save as before.
      const smartOwned = <T,>(value: T) => (isSmartManaged ? undefined : value);

      const params: UpdatePensionByEmployeeIdParams = {
        pensionCalculationType: values.pensionCalculationType?.value || null,
        isSalarySacrifice: values.isSalarySacrifice ?? null,
        erPensionDeductionMethod:
          values.erPensionDeductionMethod?.value || null,
        eePensionDeductionMethod:
          values.eePensionDeductionMethod?.value || null,
        erPensionPercentage: smartOwned(
          percentToSave(values.erPensionPercentage),
        ),
        eePensionPercentage: smartOwned(
          percentToSave(values.eePensionPercentage),
        ),
        erPensionFixedAmount: toNumber(values.erPensionFixedAmount),
        eePensionFixedAmount: toNumber(values.eePensionFixedAmount),
        avcEEContribution: toNumber(values.avcEEContribution),
        avcERContribution: toNumber(values.avcERContribution),
        refundPaidDate: values.refundPaidDate
          ? moment(values.refundPaidDate).toISOString()
          : (null as any),
        refundDue: values.refundDue ?? null,
        refundPaidEE: toNumber(values.refundPaidEE),
        refundPaidER: toNumber(values.refundPaidER),
        category: values.category?.value || null,
        assessStatus: values.assessStatus || null,
        dateOnClassifiedOn: values.dateOnClassifiedOn
          ? moment(values.dateOnClassifiedOn).toISOString()
          : null,
        lastAssessmentOn: values.lastAssessmentOn
          ? moment(values.lastAssessmentOn).toISOString()
          : null,
        pensionProviderId: values.pensionProviderId?.value || null,
        pensionSchemeId: values.pensionSchemeId || null,
        pensionSchemeName: values.pensionSchemeName || null,
        nestEmployerRef: values.nestEmployerRef || null,
        memberId: values.memberId || null,
        postponementEndDate: values.postponementEndDate
          ? moment(values.postponementEndDate).toISOString()
          : null,
        membershipStartDate: values.membershipStartDate
          ? moment(values.membershipStartDate).toISOString()
          : null,
        enrolmentType: values.enrolmentType || null,
        otpState: values.otpState?.value || null,
        otpInDate: values.otpInDate
          ? moment(values.otpInDate).toISOString()
          : null,
        // NX-5520: "Company default" ('') is saved as null. NEST takes the
        // pension method from the employee's group.
        ...(isNestPension
          ? { nestGroupOverride: values.nestGroupOverride?.value || null }
          : {
              pensionMethod: values.pensionMethod?.value || null,
            }),
        nestPaymentSource: values.nestPaymentSource || null,
        otpOutDate: values.otpOutDate
          ? moment(values.otpOutDate).toISOString()
          : null,
        // NX-3476: passed through untouched — Nest matches the reference against
        // its own record, so trimming or reformatting would break the match.
        ...(values.otpState?.value === 'opted_out' && values.optOutReference
          ? { optOutReference: values.optOutReference }
          : {}),
      };

      // The warning is about diverging from the COMPANY rates: once an
      // employee's % differs from the company setting they stop following
      // company-level updates. So the baseline is the company value, compared
      // the same way the form prefills (own column → company → statutory).
      // Confirm only when this save actually creates or moves a divergence:
      // an untouched already-custom employee, or a value pulled back in line
      // with the company, needs no warning.
      const divergesFromCompany = (
        submitted: number,
        ownValue: number | null | undefined,
        companyValue: number | null | undefined,
      ) => {
        if (typeof companyValue !== 'number') return false;
        const effective =
          typeof ownValue === 'number' ? ownValue : companyValue;
        return submitted !== companyValue && submitted !== effective;
      };

      // Nothing to confirm when SMART owns the numbers — the user cannot have
      // changed them, and they are not part of the payload either.
      // A cleared input follows the company, so it never diverges.
      const erChanged =
        !isSmartManaged &&
        !isBlankPercent(values.erPensionPercentage) &&
        divergesFromCompany(
          toNumber(values.erPensionPercentage),
          pensionData?.erPensionPercentage,
          defaultPercents.er,
        );
      const eeChanged =
        !isSmartManaged &&
        !isBlankPercent(values.eePensionPercentage) &&
        divergesFromCompany(
          toNumber(values.eePensionPercentage),
          pensionData?.eePensionPercentage,
          defaultPercents.ee,
        );

      if (erChanged || eeChanged) {
        Modal.confirm({
          title:
            messages['common.doYouWantToProceed'] ?? 'Do you want to proceed?',
          content:
            messages['common.pensionPercentageWarning'] ??
            'Employees with custom pension percentages will not be updated.',
          okText: messages['common.confirm'] ?? 'Confirm',
          cancelText: messages['common.cancel'] ?? 'Cancel',
          onOk: () => doSave(params),
        });
        return;
      }

      await doSave(params);
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSave, isUpdatingPension, !readOnly);

  return (
    <div className="relative">
      {isFetchingPension && (
        <div className="absolute inset-0 z-10 bg-white/60">
          <AppLoader />
        </div>
      )}
      {!readOnly && !inDrawer && isFetchSmartPension && (
        <div className="flex items-center justify-end gap-x-[16px] mb-4">
          <AppButton
            variant="outlined"
            name={messages['common.syncPensionData'] ?? 'Sync Pension Data'}
            onClick={handleFetchSmartPensionForEmployee}
            loading={isFetchingSmartPension}
          />
        </div>
      )}
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.pension']}
      </Typography.Title>

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        disabled={readOnly}
      >
        {/* ===== Auto Enrolment Status ===== */}
        <div className="text-[16px] font-medium mb-4">
          {messages['common.autoEnrollmentStatus']}
        </div>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.category']} name="category">
              <AppSelect
                options={categoryPensionOptions}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.status']} name="assessStatus">
              <AppInput placeholder={messages['form.enter']} disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.dateOnClassifiedOn']}
              name="dateOnClassifiedOn"
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.lastAssessmentDoneOn']}
              name="lastAssessmentOn"
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.pensionProviderName']}
              name="pensionProviderId"
            >
              <AppSelect
                options={pensionProviderIdOptions}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.membershipId']} name="memberId">
              <AppInput
                placeholder={messages['form.enter']}
                disabled={readOnly || isNestManaged}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Provider-specific auto-enrolment fields */}
        {isNestPension
          ? renderNestAutoEnrolment()
          : isSmartPension
            ? renderSmartAutoEnrolment()
            : renderNowLikeAutoEnrolment()}

        {/* ===== Calculation Method, Pension Method & Salary Sacrifice ===== */}
        <Divider />
        <div className="text-[16px] font-medium mb-4">
          {`${messages['common.calculationMethod']}, ${messages['common.pensionMethod']} & ${messages['common.salarySacrifice']}`}
        </div>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.calculationMethod']}
              name="pensionCalculationType"
            >
              <AppSelect
                options={pensionCalCulationMethodOptions}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
          {/* NX-5520: only this employee is calculated with the method picked
              here; "Company default" follows the company's method. */}
          <Col xs={24} sm={12}>
            {isNestPension ? (
              <Form.Item
                label={messages['common.pensionMethod']}
                extra="Set by the employee's NEST group."
              >
                <AppInput
                  value={pensionMethodLabel(nestGroupSettings?.pensionMethod)}
                  disabled
                />
              </Form.Item>
            ) : (
              <Form.Item
                label={messages['common.pensionMethod']}
                name="pensionMethod"
              >
                <AppSelect
                  options={[
                    companyDefaultMethodOption(
                      employeeData?.companyPayment?.autoEnrolSettings
                        ?.pensionMethod,
                    ),
                    ...defaultPensionMethodOptions,
                  ]}
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                  allowClear={false}
                />
              </Form.Item>
            )}
          </Col>
          {/* Salary sacrifice is a payroll setting, so every provider has it.
              Yes/No rather than a switch: leaving it unset keeps following
              the company setting, as on that page. */}
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.salarySacrifice']}
              name="isSalarySacrifice"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== Pension Deduction Method ===== */}
        <Divider />
        <div className="text-[16px] font-medium mb-4">
          {messages['common.pensionDeductionMethod']}
        </div>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employerDeductionMethod']}
              name="erPensionDeductionMethod"
              tooltip={
                isSmartManaged
                  ? (messages['common.smartPensionManaged'] ??
                    'Managed by Smart Pension')
                  : undefined
              }
            >
              <AppSelect
                options={pensionDeductionMethods}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
                disabled={readOnly || isSmartManaged}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employeeDeductionMethod']}
              name="eePensionDeductionMethod"
              tooltip={
                isSmartManaged
                  ? (messages['common.smartPensionManaged'] ??
                    'Managed by Smart Pension')
                  : undefined
              }
            >
              <AppSelect
                options={pensionDeductionMethods}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
                disabled={readOnly || isSmartManaged}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            {erMethod === PensionDeductionMethodsEnum.PERCENTAGE ? (
              <Form.Item
                label={messages['common.employer']}
                name="erPensionPercentage"
                rules={[
                  {
                    validator: (_, value) => {
                      const er = parseFloat(value);
                      if (value && !isNaN(er) && er < pensionMins.erMin) {
                        return Promise.reject(
                          `Employer contribution must be at least ${pensionMins.erMin}%`,
                        );
                      }
                      if (value && !isNaN(er) && (er < 0 || er > 100)) {
                        return Promise.reject(
                          'Please enter a value between 0% and 100%',
                        );
                      }
                      const eeValue = form.getFieldValue('eePensionPercentage');
                      const ee = parseFloat(eeValue);
                      // Both blank = nothing configured at any level; the
                      // fields are omitted from the payload, so there is no
                      // zero to reject.
                      const bothBlank = !value && !eeValue;
                      if (!bothBlank && (er || 0) + (ee || 0) === 0) {
                        return Promise.reject(
                          'Total employer and employee pension contributions cannot be zero.',
                        );
                      }
                      if (
                        !isNaN(er) &&
                        !isNaN(ee) &&
                        er + ee < pensionMins.totalMin
                      ) {
                        return Promise.reject(
                          `Total employer & employee contribution must be at least ${pensionMins.totalMin}%`,
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                dependencies={['eePensionPercentage']}
              >
                <AppPercentageInput
                  placeholder={messages['form.enter']}
                  disabled={readOnly || isSmartManaged}
                />
              </Form.Item>
            ) : erMethod === PensionDeductionMethodsEnum.FIXED ? (
              <Form.Item
                label={messages['common.employer']}
                name="erPensionFixedAmount"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (value && Number(value) < 0) {
                        throw new Error(
                          messages['form.mustBeZeroOrMore'] ??
                            'Must be 0 or more',
                        );
                      }
                    },
                  },
                ]}
              >
                <AppCurrencyInput
                  prefix={currency}
                  placeholder={messages['form.enter']}
                  disabled={readOnly || isSmartManaged}
                />
              </Form.Item>
            ) : null}
          </Col>
          <Col xs={24} sm={12}>
            {eeMethod === PensionDeductionMethodsEnum.PERCENTAGE ? (
              <Form.Item
                label={messages['common.employee']}
                name="eePensionPercentage"
                rules={[
                  {
                    validator: (_, value) => {
                      const ee = parseFloat(value);
                      if (value && !isNaN(ee) && (ee < 0 || ee > 100)) {
                        return Promise.reject(
                          'Please enter a value between 0% and 100%',
                        );
                      }
                      const erValue = form.getFieldValue('erPensionPercentage');
                      const er = parseFloat(erValue);
                      const bothBlank = !value && !erValue;
                      if (!bothBlank && (er || 0) + (ee || 0) === 0) {
                        return Promise.reject(
                          'Total employer and employee pension contributions cannot be zero.',
                        );
                      }
                      if (
                        !isNaN(er) &&
                        !isNaN(ee) &&
                        er + ee < pensionMins.totalMin
                      ) {
                        return Promise.reject(
                          `Total employer & employee contribution must be at least ${pensionMins.totalMin}%`,
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                dependencies={['erPensionPercentage']}
              >
                <AppPercentageInput
                  placeholder={messages['form.enter']}
                  disabled={readOnly || isSmartManaged}
                />
              </Form.Item>
            ) : eeMethod === PensionDeductionMethodsEnum.FIXED ? (
              <Form.Item
                label={messages['common.employee']}
                name="eePensionFixedAmount"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (value && Number(value) < 0) {
                        throw new Error(
                          messages['form.mustBeZeroOrMore'] ??
                            'Must be 0 or more',
                        );
                      }
                    },
                  },
                ]}
              >
                <AppCurrencyInput
                  prefix={currency}
                  placeholder={messages['form.enter']}
                  disabled={readOnly || isSmartManaged}
                />
              </Form.Item>
            ) : null}
          </Col>
        </Row>

        {/* ===== Additional Voluntary Contribution ===== */}
        <Divider />
        <div className="text-[16px] font-medium mb-4">
          {messages['common.additionalVoluntaryContribution']}
        </div>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employerContribution']}
              name="avcERContribution"
            >
              <AppCurrencyInput
                placeholder={messages['form.enter']}
                prefix={currency}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employeeContribution']}
              name="avcEEContribution"
            >
              <AppCurrencyInput
                placeholder={messages['form.enter']}
                prefix={currency}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== Pension Refund ===== */}
        <Divider />
        <div className="text-[16px] font-medium mb-4">
          {messages['common.pensionRefund']}
        </div>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.refundMadeDate']}
              name="refundPaidDate"
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format={'DD/MM/YYYY'}
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.refundDue'] ?? 'Refund Due?'}>
              <Form.Item name="refundDue" valuePropName="checked" noStyle>
                <AppSwitch disabled />
              </Form.Item>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employerContribution']}
              name="refundPaidER"
            >
              <AppCurrencyInput
                placeholder={messages['form.enter']}
                prefix={currency}
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.employeeContribution']}
              name="refundPaidEE"
            >
              <AppCurrencyInput
                placeholder={messages['form.enter']}
                prefix={currency}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== Main Pension Contributions YTD ===== */}
        <Divider />
        <CollapsibleYTDTable
          title={messages['common.mainPensionContributionYTD']}
          isOpen={showMainYTD}
          onToggle={() => setShowMainYTD(!showMainYTD)}
          data={pensionData}
          eeKey="mainEEPension"
          erKey="mainERPension"
          messages={messages}
          currency={currency}
        />

        {/* ===== Additional Pension Contributions YTD ===== */}
        <div className="mt-2">
          <CollapsibleYTDTable
            title={
              messages['common.additionalVoluntaryContributionYTD'] ??
              'Additional Pension Contributions YTD'
            }
            isOpen={showAvcYTD}
            onToggle={() => setShowAvcYTD(!showAvcYTD)}
            data={pensionData}
            eeKey="avcEEPension"
            erKey="avcERPension"
            messages={messages}
            currency={currency}
          />
        </div>

        {/* Action Buttons */}
        {!readOnly && !inDrawer && (
          <div className="flex items-center justify-start pt-6">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={handleSave}
              loading={isUpdatingPension}
            />
          </div>
        )}
      </AppFormContainer>
    </div>
  );
};

const CollapsibleYTDTable = ({
  title,
  isOpen,
  onToggle,
  data,
  eeKey,
  erKey,
  messages,
  currency,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  data?: IPension;
  eeKey: string;
  erKey: string;
  messages: Record<string, string>;
  currency: string;
}) => {
  const obEE = toNumber((data?.pensionOpeningBalance as any)?.[eeKey]);
  const obER = toNumber((data?.pensionOpeningBalance as any)?.[erKey]);
  const prevEE = toNumber((data?.previousYTD as any)?.[eeKey]);
  const prevER = toNumber((data?.previousYTD as any)?.[erKey]);
  const currEE = toNumber((data?.currentYTD as any)?.[eeKey]);
  const currER = toNumber((data?.currentYTD as any)?.[erKey]);

  const rows = [
    {
      label: `(A) ${messages['common.fromPreviousSystem'] ?? 'From Previous System'}`,
      ee: formatMoney(obEE, currency),
      er: formatMoney(obER, currency),
    },
    {
      label: `(B) ${messages['common.previousYearTotal'] ?? 'Previous Year Total'}`,
      ee: formatMoney(prevEE, currency),
      er: formatMoney(prevER, currency),
    },
    {
      label: `(C) ${messages['common.thisYearTotal'] ?? 'This year Total'}`,
      ee: formatMoney(currEE, currency),
      er: formatMoney(currER, currency),
    },
    {
      label: `(A + B + C) ${messages['common.totalSoFar'] ?? 'Total so far'}`,
      ee: formatMoney(obEE + prevEE + currEE, currency),
      er: formatMoney(obER + prevER + currER, currency),
      bold: true,
    },
  ];

  return (
    <div className="bg-[#F3F4F8] rounded-[8px] p-4">
      <div
        className="flex items-center gap-2 cursor-pointer select-none"
        onClick={onToggle}
      >
        {isOpen ? (
          <DownOutlined className="text-[12px] text-[#1A1D1F]" />
        ) : (
          <RightOutlined className="text-[12px] text-[#1A1D1F]" />
        )}
        <span className="text-[16px] font-semibold text-[#1A1D1F]">
          {title}
        </span>
      </div>
      {isOpen && (
        <div className="mt-4 bg-white rounded-[8px] border border-[#E4E4E7] overflow-hidden">
          <table className="w-full text-[14px] border-collapse">
            <thead>
              <tr className="bg-[#EDF0F7]">
                <th className="text-left py-3 px-4 font-normal" />
                <th className="text-left py-3 px-4 font-semibold text-[#1A1D1F] w-[120px]">
                  {messages['common.employee'] ?? 'Employee'}
                </th>
                <th className="text-left py-3 px-4 font-semibold text-[#1A1D1F] w-[120px]">
                  {messages['common.employer'] ?? 'Employer'}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className={`border-t border-[#E4E4E7] ${row.bold ? 'font-semibold' : ''}`}
                >
                  <td className="py-3 px-4 text-[#3E454D]">{row.label}</td>
                  <td className="py-3 px-4 text-[#1A1D1F]">{row.ee}</td>
                  <td className="py-3 px-4 text-[#1A1D1F]">{row.er}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Pension;
